<div
  class="table-stack"
  [class.table-stack--intrinsic-height]="useIntrinsicTableBodyHeight"
>
  <!-- PrimeNG Table -->
  <p-table
    #table
    [value]="data"
    [paginator]="false"
    [rows]="rows"
    [totalRecords]="totalCount || 0"
    [loading]="isLoading && !skeletonVisible"
    [class.d-none]="!loadedOnce && !isLoading && !isInitialized"
    [selection]="disableSelection ? null : selectedItems"
    [dataKey]="dataKey"
    [resizableColumns]="true"
    columnResizeMode="expand"
    [filterDelay]="filterDelay"
    (selectionChange)="
      disableSelection === true ? null : onSelectionChange($event)
    "
    [selectionPageOnly]="true"
    [lazy]="true"
    (onSort)="onSort($event)"
    [editMode]="'cell'"
    [expandedRowKeys]="expandedRows"
    (onRowExpand)="rowExpand.emit($event)"
    (onRowCollapse)="rowCollapse.emit($event)"
    (onEditComplete)="itemUpdate.emit($event.data)"
    (onColResize)="onColumnResize($event)"
    [style.--table-wrapper-padding-bottom]="
      data.length > 0 ? bottomPadding : '0px'
    "
    [scrollable]="false"
    [rowTrackBy]="trackByRowId"
  >
    <!-- Header with column titles -->
    <ng-template pTemplate="header">
      <!-- First header row -->
      <tr>
        <th
          pResizableColumn
          class="expansion-cell"
          style="width: 3rem"
          *ngIf="expansionTemplate"
          [attr.rowspan]="hasColumnGroups() ? 2 : 1"
          style="width: 3rem"
        ></th>
        <th
          *ngIf="!disableSelection"
          pResizableColumn
          [attr.rowspan]="hasColumnGroups() ? 2 : 1"
          style="text-align: center; width: 3rem"
        >
          <p-tableHeaderCheckbox />
        </th>

        <!-- Iterate through ALL columns in order -->
        <ng-container
          *ngFor="
            let column of columns();
            let colIndex = index;
            trackBy: trackByField
          "
        >
          <!-- Non-grouped columns: render in first row with rowspan -->
          <th
            *ngIf="!column.groupKey"
            pResizableColumn
            [attr.data-column-index]="colIndex"
            [pSortableColumn]="
              column.enableSorting === true && column.field ? column.field : ''
            "
            [ngClass]="column.cssClass ?? ''"
            [style.width.px]="getColumnDisplayWidth(column, colIndex)"
            [style.max-width.px]="column.maxWidth ?? null"
            [attr.rowspan]="hasColumnGroups() ? 2 : 1"
            [pTooltip]="column.tooltipLabel"
            tooltipPosition="top"
          >
            {{ column.label }}
            <p-sortIcon
              *ngIf="column.enableSorting === true && column.field"
              [field]="column.field"
            ></p-sortIcon>
          </th>

          <!-- Grouped columns: render group header only once per group -->
          <th
            *ngIf="column.groupKey && isFirstInGroup(column)"
            pResizableColumn
            [attr.colspan]="getColumnsByGroup(column.groupKey).length"
            class="column-group-header"
            style="text-align: center"
            [style.width.px]="getGroupDisplayWidth(column.groupKey)"
            [style.min-width.px]="getGroupDisplayWidth(column.groupKey)"
          >
            {{ getGroupLabel(column.groupKey) }}
          </th>
        </ng-container>

        <th
          *ngIf="
            showEditButton ||
            showViewButton ||
            showDeleteButton ||
            showResetButton ||
            showCreateButton
          "
          pResizableColumn
          class="sticky-action-column"
          [style.width.px]="actionsColWidth"
          [style.min-width.px]="actionsColWidth"
          [style.max-width.px]="actionsColWidth"
          [attr.rowspan]="hasColumnGroups() ? 2 : 1"
        >
          <div class="d-flex justify-content-center">
            <op-button-ng
              *ngIf="showResetButton"
              [outlined]="!isFiltered"
              (buttonClick)="resetGrid()"
              [label]="'Reset' | translate"
              icon="fas fa-eraser"
            />
            <op-button-ng
              *ngIf="showCreateButton && !showResetButton"
              [outlined]="true"
              (buttonClick)="itemCreate.emit()"
              [label]="'Create' | translate"
              icon="fas fa-plus"
            />
          </div>
        </th>
      </tr>

      <!-- Second header row (only for grouped columns) -->
      <tr *ngIf="hasColumnGroups()">
        <ng-container
          *ngFor="
            let column of columns();
            let colIndex = index;
            trackBy: trackByField
          "
        >
          <th
            *ngIf="column.groupKey"
            pResizableColumn
            [attr.data-column-index]="colIndex"
            [pSortableColumn]="
              column.enableSorting === true && column.field ? column.field : ''
            "
            [style.width.px]="getColumnDisplayWidth(column, colIndex)"
            [style.max-width.px]="column.maxWidth ?? null"
            class="column-group-child"
            [ngClass]="{
              'first-in-group': isFirstInGroup(column),
              'last-in-group': isLastInGroup(column),
            }"
            [pTooltip]="column.tooltipLabel"
            tooltipPosition="top"
          >
            {{ column.label }}
            <p-sortIcon
              *ngIf="column.enableSorting === true && column.field"
              [field]="column.field"
            ></p-sortIcon>
          </th>
        </ng-container>
      </tr>
      <!-- Filter row -->
      <tr *ngIf="hasFilterableColumns()">
        <th
          class="expansion-cell"
          style="width: 3rem"
          *ngIf="expansionTemplate"
        ></th>
        <th style="width: 3rem" *ngIf="!disableSelection"></th>

        <!-- Unified filters for all columns (respects column order regardless of grouping) -->
        <ng-container *ngFor="let column of columns(); trackBy: trackByField">
          <th [ngClass]="column.cssClass ?? ''">
            <div
              class="table-header-filters-wrap"
              [attr.inert]="isLoading ? '' : null"
              [class.table-header-filters-wrap--loading]="isLoading"
            >
              <!-- Dropdown filter (for enableHeaderFiltering) -->
              <p-dropdown
                *ngIf="
                  column.enableHeaderFiltering &&
                  column.dataSourceKey &&
                  !column.multiSelect
                "
                styleClass="table-filter-dropdown"
                [panelStyleClass]="'table-filter-overlay-panel'"
                [options]="dropdownOptions?.[column.dataSourceKey] ?? []"
                [virtualScroll]="true"
                [virtualScrollItemSize]="42"
                [lazy]="true"
                (onLazyLoad)="onDropdownLazyLoad($event, column)"
                [optionLabel]="column.dataDisplayField || 'name'"
                [filterBy]="column.dataDisplayField || 'name'"
                [(ngModel)]="dropdownFilterValues[column.field]"
                (ngModelChange)="
                  onDropdownFilterChange(
                    $event,
                    column.field,
                    column.dataKeyField || '_id'
                  )
                "
                [filter]="true"
                (onFilter)="
                  onDropdownFilter(
                    $event,
                    column.dataSourceKey,
                    column.dataKeyField,
                    column.dataDisplayField
                  )
                "
                (onShow)="onDropdownShow(column.dataSourceKey, column)"
                [showClear]="true"
                [filter]="true"
                [filterBy]="column.dataDisplayField || 'name'"
                [placeholder]="'Select' | translate"
                appendTo="body"
                [emptyFilterMessage]="'No result found' | translate"
                [emptyMessage]="'No result found' | translate"
                scrollHeight="250px"
              >
                <ng-template let-item pTemplate="item">
                  {{
                    (column.dataDisplayField || '').includes('|')
                      ? parseDisplayField(item, column.dataDisplayField)
                      : (item | get: column.dataDisplayField || 'name')
                  }}
                </ng-template>
                <ng-template let-item pTemplate="selectedItem">
                  {{
                    (column.dataDisplayField || '').includes('|')
                      ? parseDisplayField(item, column.dataDisplayField)
                      : (item | get: column.dataDisplayField || 'name')
                  }}
                </ng-template>
              </p-dropdown>

              <p-multiSelect
                *ngIf="
                  column.enableHeaderFiltering &&
                  dropdownOptions &&
                  column.dataSourceKey &&
                  dropdownOptions[column.dataSourceKey] &&
                  column.multiSelect
                "
                styleClass="table-filter-dropdown"
                [panelStyleClass]="
                  'table-filter-overlay-panel table-filter-overlay-panel--multi'
                "
                [options]="dropdownOptions[column.dataSourceKey] || []"
                [optionLabel]="column.dataDisplayField || 'name'"
                [filterBy]="column.dataDisplayField || 'name'"
                [(ngModel)]="dropdownFilterValues[column.field]"
                (ngModelChange)="
                  onDropdownFilterChange(
                    $event,
                    column.field,
                    column.dataKeyField || '_id'
                  )
                "
                [filter]="true"
                (onFilter)="
                  onDropdownFilter(
                    $event,
                    column.dataSourceKey,
                    column.dataKeyField,
                    column.dataDisplayField
                  )
                "
                (onPanelShow)="onDropdownShow(column.dataSourceKey, column)"
                [showClear]="true"
                [placeholder]="'Select' | translate"
                appendTo="body"
                [emptyFilterMessage]="'No result found' | translate"
                [emptyMessage]="'No result found' | translate"
                [display]="'comma'"
                [showToggleAll]="true"
                [maxSelectedLabels]="2"
              >
                <ng-template let-item pTemplate="item">
                  {{
                    (column.dataDisplayField || '').includes('|')
                      ? parseDisplayField(item, column.dataDisplayField)
                      : (item | get: column.dataDisplayField || 'name')
                  }}
                </ng-template>
              </p-multiSelect>

              <!-- Date filter with calendar icon -->
              <div
                *ngIf="
                  !column.enableHeaderFiltering &&
                  column.enableFiltering === true &&
                  column.field &&
                  column.type === 'date'
                "
                class="date-filter-container"
              >
                <op-calendar
                  #calendar
                  [field]="column.field"
                  (operatorSelect)="primeOperator = $event"
                  [clearButton]="true"
                  [tableFilters]="tableFilters()"
                  (filtersSelect)="onDateSelect($event, column.field)"
                />
              </div>

              <!-- Boolean filter -->
              <p-dropdown
                *ngIf="
                  !column.enableHeaderFiltering &&
                  column.enableFiltering === true &&
                  column.field &&
                  column.type === 'boolean'
                "
                [options]="[
                  { label: 'Yes' | translate, value: true },
                  { label: 'No' | translate, value: false },
                  { label: 'All-s' | translate, value: null },
                ]"
                [(ngModel)]="dropdownFilterValues[column.field]"
                (ngModelChange)="
                  onDropdownFilterChange($event, column.field, '')
                "
                [showClear]="true"
                [placeholder]="'All-s' | translate"
                appendTo="body"
                optionLabel="label"
                optionValue="value"
              >
              </p-dropdown>

              <!-- Text filter (for non-date, non-boolean columns) -->
              <div
                class="table-filter-numeric-row w-100"
                [class.calendar-filter-group]="numberOperatorShell(column)"
                [class.d-flex]="!numberOperatorShell(column)"
                [class.gap-1]="!numberOperatorShell(column)"
                [class.align-items-center]="!numberOperatorShell(column)"
              >
                <ng-container *ngIf="numberOperatorShell(column)">
                  <span
                    class="operator-addon"
                    (click)="numberOpDropdown.show($event)"
                    >{{ numberFilterOperators[column.field!] || '=' }}</span
                  >
                  <p-dropdown
                    #numberOpDropdown
                    [options]="numberOperatorOptions"
                    [(ngModel)]="numberFilterOperators[column.field!]"
                    (ngModelChange)="
                      onNumberOperatorChange($event, column.field!)
                    "
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    [showClear]="false"
                    [style]="{ position: 'absolute', visibility: 'hidden' }"
                    styleClass="operator-dropdown"
                  >
                  </p-dropdown>
                </ng-container>

                <div
                  [class]="
                    !column.enableHeaderFiltering &&
                    column.enableFiltering === true &&
                    column.enableOperatorFiltering === true &&
                    column.field &&
                    column.type === 'number'
                      ? 'table-filter-value flex-grow-1 min-w-0'
                      : ''
                  "
                >
                  <p-columnFilter
                    #textFilter
                    *ngIf="
                      !column.enableHeaderFiltering &&
                      column.enableFiltering === true &&
                      column.field &&
                      column.type !== 'date' &&
                      column.type !== 'boolean'
                    "
                    display="row"
                    appendTo="body"
                    type="text"
                    [showMenu]="false"
                    [showClearButton]="false"
                    [field]="column.field"
                    (input)="
                      column.type === 'number'
                        ? onNumberFilterChange($event, column.field)
                        : onFilterInput($event, column.field, column.operator)
                    "
                    (keydown.enter)="applyFiltersNow()"
                  >
                  </p-columnFilter>
                </div>
              </div>
            </div>
          </th>
        </ng-container>

        <!-- Actions column -->
        <th
          *ngIf="showActionsColumn()"
          class="sticky-action-column"
          [style.width.px]="actionsColWidth"
          [style.min-width.px]="actionsColWidth"
          [style.max-width.px]="actionsColWidth"
        ></th>
      </tr>
    </ng-template>

    <!-- Body -->
    <ng-template
      pTemplate="body"
      let-item
      let-expanded="expanded"
      let-rowIndex="rowIndex"
    >
      <tr [pSelectableRow]="item" [ngClass]="rowClass ? rowClass(item) : ''">
        <td *ngIf="expansionTemplate" class="expansion-cell">
          <div
            class="expansion-cell-inner d-flex justify-content-center align-items-center"
          >
            <op-button-ng
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              [pRowToggler]="item"
              [ghost]="true"
            />
          </div>
        </td>
        <td *ngIf="!disableSelection" style="text-align: center">
          <p-tableCheckbox [value]="item" />
        </td>
        <td
          *ngFor="
            let column of columns();
            let colIndex = index;
            trackBy: trackByField
          "
          [pEditableColumn]="column.enableEditing && editable ? item : null"
          [style.width.px]="getColumnDisplayWidth(column, colIndex)"
          [style.max-width.px]="column.maxWidth ?? null"
          [ngClass]="column.cssClass ?? ''"
          [class.text-center]="column.align === 'center'"
          [class.text-left]="column.align === 'left'"
          [class.text-right]="column.align === 'right'"
        >
          <p-cellEditor *ngIf="column.enableEditing && editable">
            <ng-template pTemplate="input">
              <!-- Boolean type - dropdown -->
              <p-dropdown
                *ngIf="column.type === 'boolean'"
                [options]="[
                  { label: 'Yes' | translate, value: true },
                  { label: 'No' | translate, value: false },
                ]"
                [(ngModel)]="item[column.field]"
                [ngModelOptions]="{ standalone: true }"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
              ></p-dropdown>

              <!-- Text input -->
              <input
                class="p-cell-editor-input"
                *ngIf="column.type !== 'number' && column.type !== 'boolean'"
                pInputText
                type="text"
                [(ngModel)]="item[column.field]"
                [ngModelOptions]="{ standalone: true }"
              />

              <!-- Number input -->
              <input
                *ngIf="column.type === 'number'"
                class="p-cell-editor-input"
                pInputText
                type="number"
                [(ngModel)]="item[column.field]"
                [ngModelOptions]="{ standalone: true }"
                (keydown)="numberParser($event, column.disableDecimals)"
              />
            </ng-template>

            <ng-template pTemplate="output">
              <div class="editable-cell-wrapper d-flex align-items-center">
                <span
                  class="flex-grow-1"
                  [class.text-center]="column.align === 'center'"
                  [class.text-left]="column.align === 'left'"
                  [class.text-right]="column.align === 'right'"
                >
                  <!-- Custom template by dynamic name (no ViewChild needed) -->
                  <ng-container
                    *ngIf="column.template && templateMap[column.template]"
                  >
                    <ng-container
                      class="p-cell-editor-output"
                      *ngTemplateOutlet="
                        templateMap[column.template];
                        context: {
                          item: item,
                          rowIndex: rowIndex,
                          value: column.field
                            ? column.field.includes('.')
                              ? (item | get: column.field)
                              : item[column.field]
                            : '',
                          column: column,
                        }
                      "
                    ></ng-container>
                  </ng-container>
                  <!-- Default display based on type -->
                  <ng-container *ngIf="!column.template">
                    <!-- Date type -->
                    <ng-container *ngIf="column.type === 'date'">
                      {{
                        item
                          | get: column.field || ''
                          | date: column.dateFormat || 'dd/MM/yyyy HH:mm'
                      }}
                    </ng-container>
                    <!-- Number type -->
                    <ng-container *ngIf="column.type === 'number'">
                      {{ item | get: column.field || '' | number }}
                    </ng-container>
                    <!-- Boolean type -->
                    <ng-container *ngIf="column.type === 'boolean'">
                      <div class="text-center">
                        <i
                          *ngIf="(item | get: column.field || '') === true"
                          class="fas fa-check text-success"
                        ></i>
                        <i
                          *ngIf="(item | get: column.field || '') === false"
                          class="fas fa-times text-danger"
                        ></i>
                      </div>
                    </ng-container>
                    <!-- String or default -->
                    <ng-container
                      *ngIf="!column.type || column.type === 'string'"
                    >
                      {{ item | get: column.field || '' }}
                    </ng-container>
                  </ng-container>
                </span>
                <i
                  class="fas fa-pencil-alt editable-icon"
                  style="
                    font-size: 0.7rem;
                    opacity: 0.4;
                    color: #6c757d;
                    flex-shrink: 0;
                  "
                ></i>
              </div>
            </ng-template>
          </p-cellEditor>
          <!-- Non-editable display for columns without enableEditing -->
          <ng-container *ngIf="!column.enableEditing || !editable">
            <!-- Custom template by dynamic name -->
            <ng-container
              *ngIf="column.template && templateMap[column.template]"
            >
              <ng-container
                *ngTemplateOutlet="
                  templateMap[column.template];
                  context: {
                    item: item,
                    rowIndex: rowIndex,
                    value: column.field
                      ? column.field.includes('.')
                        ? (item | get: column.field)
                        : item[column.field]
                      : '',
                    column: column,
                  }
                "
              ></ng-container>
            </ng-container>
            <!-- Default display based on type -->
            <ng-container *ngIf="!column.template">
              <!-- Date type -->
              <ng-container *ngIf="column.type === 'date'">
                {{
                  item
                    | get: column.field || ''
                    | date: column.dateFormat || 'dd/MM/yyyy HH:mm'
                }}
              </ng-container>
              <!-- Number type -->
              <ng-container *ngIf="column.type === 'number'">
                {{ item | get: column.field || '' | number }}
              </ng-container>
              <!-- Boolean type -->
              <ng-container *ngIf="column.type === 'boolean'">
                <div class="text-center">
                  <i
                    *ngIf="(item | get: column.field || '') === true"
                    class="fas fa-check text-success"
                  ></i>
                  <i
                    *ngIf="(item | get: column.field || '') === false"
                    class="fas fa-times text-danger"
                  ></i>
                </div>
              </ng-container>
              <!-- String or default -->
              <ng-container *ngIf="!column.type || column.type === 'string'">
                <!-- With data lookup -->
                <ng-container
                  *ngIf="
                    dataLookups &&
                    column.dataSourceKey &&
                    dataLookups[column.dataSourceKey]
                  "
                >
                  {{
                    dataLookups[column.dataSourceKey]
                      | find
                        : column.dataKeyField || '_id'
                        : (item | get: column.field || '')
                        : column.dataDisplayField || 'name'
                        : ' '
                  }}
                </ng-container>
                <!-- Without data lookup (default) -->
                <ng-container *ngIf="!column.dataSourceKey">
                  {{ item | get: column.field || '' }}
                </ng-container>
              </ng-container>
            </ng-container>
          </ng-container>
        </td>
        <td
          *ngIf="showActionsColumn()"
          class="sticky-action-column"
          [style.width.px]="actionsColWidth"
          [style.min-width.px]="actionsColWidth"
          [style.max-width.px]="actionsColWidth"
        >
          <!-- Default actions buttons -->
          <div class="d-flex gap-1 align-items-center">
            <op-button-ng
              *ngIf="editable && showEditButton"
              class="ml-1"
              [ghost]="true"
              [square]="true"
              icon="fad fa-pen"
              (buttonClick)="
                showQuickViewButton
                  ? itemQuickView.emit(item)
                  : itemEdit.emit(item)
              "
              [routerLink]="rowFragment(item, pencilAction) ? '.' : undefined"
              [fragment]="rowFragment(item, pencilAction)"
              [tooltipLabel]="('Edit' | translate) + ' ' + parseEntityName()"
            />
            <op-button-ng
              *ngIf="showQuickViewButton"
              class="ml-1"
              [size]="quickViewButtonSize"
              [ghost]="!!!quickViewButtonLabel"
              [square]="!!!quickViewButtonLabel"
              [label]="quickViewButtonLabel ?? ''"
              [icon]="
                quickViewButtonLabel
                  ? undefined
                  : (quickViewButtonIcon ?? 'fad fa-eye')
              "
              [color]="
                quickViewButtonLabel
                  ? undefined
                  : (quickViewButtonColor ?? 'info')
              "
              (buttonClick)="itemEdit.emit(item)"
              [routerLink]="rowFragment(item, 'edit') ? '.' : undefined"
              [fragment]="rowFragment(item, 'edit')"
              [tooltipLabel]="
                quickViewButtonLabel
                  ? undefined
                  : (quickViewButtonTooltip ??
                    ('Quick view' | translate) + ' ' + parseEntityName())
              "
            />
            <op-button-ng
              *ngIf="showExtraButton"
              class="ml-1"
              [size]="extraButtonSize ?? 'small'"
              [ghost]="true"
              [square]="true"
              [icon]="extraButtonIcon ?? 'fad fa-eye'"
              [color]="extraButtonColor ?? 'info'"
              (buttonClick)="itemExtraAction.emit(item)"
              [tooltipLabel]="extraButtonTooltip"
            />
            <op-button-ng
              *ngIf="!editable && showViewButton"
              class="ml-1"
              [ghost]="true"
              [square]="true"
              icon="fas fa-eye"
              (buttonClick)="itemsSelect.emit([item])"
              [routerLink]="rowFragment(item, 'view') ? '.' : undefined"
              [fragment]="rowFragment(item, 'view')"
              [tooltipLabel]="('Open' | translate) + ' ' + parseEntityName()"
            />
            <op-menu-ng
              *ngIf="rowMenuItems"
              [ghost]="true"
              [disabled]="isLoading"
              [items]="rowMenuItems!(item)"
            />
            <op-button-ng
              *ngIf="!rowMenuItems && editable && showDeleteButton"
              class="ml-1"
              color="danger"
              [ghost]="true"
              [square]="true"
              [icon]="
                shouldShowAltDelete(item) && altDeleteIcon
                  ? altDeleteIcon
                  : 'fad fa-trash-alt'
              "
              (buttonClick)="
                shouldShowAltDelete(item) && altDeleteFunc
                  ? altDeleteFunc(item)
                  : itemDelete.emit(item)
              "
              [tooltipLabel]="
                shouldShowAltDelete(item) && altDeleteTooltip
                  ? altDeleteTooltip
                  : ('Delete' | translate) + ' ' + parseEntityName()
              "
            />
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Row Expansion -->
    <ng-template pTemplate="rowexpansion" let-item>
      <tr *ngIf="expansionTemplate">
        <td
          [attr.colspan]="expansionRowColspan"
          style="display: table-cell !important; width: 100% !important"
        >
          <div class="p-table-expansion-shell">
            <ng-container
              *ngTemplateOutlet="expansionTemplate; context: { item: item }"
            />
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Skeleton (first load only) -->
    <ng-template pTemplate="emptymessage">
      <ng-container *ngIf="skeletonVisible">
        <tr
          *ngFor="let _ of skeletonRows"
          class="skeleton-row"
          aria-hidden="true"
        >
          <td *ngIf="expansionTemplate" class="expansion-cell"></td>
          <td
            *ngIf="!disableSelection"
            class="skeleton-cell skeleton-cell--center"
          >
            <p-skeleton width="1rem" height="1rem" />
          </td>
          <td
            *ngFor="let column of columns(); trackBy: trackByField"
            class="skeleton-cell"
            [ngClass]="column.cssClass ?? ''"
          >
            <p-skeleton [width]="skeletonWidth(column)" height="1rem" />
          </td>
          <td
            *ngIf="showActionsColumn()"
            class="sticky-action-column skeleton-cell"
          ></td>
        </tr>
      </ng-container>
    </ng-template>

    <!-- Column templates -->
    <ng-template #abcClassBadge let-value="value">
      <span class="badge badge-class badge-{{ value | abcClass }}">
        {{ value }}
      </span>
    </ng-template>

    <ng-template #aisle let-item="item">
      {{ item | locationKind: 'AISLE' }}
    </ng-template>

    <ng-template #area let-item="item">
      {{ item | locationKind: 'AREA' }}
    </ng-template>

    <ng-template #attributeMultiSelect let-value="value" let-column="column">
      {{ parseMultiSelectAttribute(value, column.dataSourceKey) }}
    </ng-template>

    <ng-template #attachments let-value="value">
      <i
        *ngIf="value?.length"
        class="fad fa-paperclip-vertical d-flex align-items-center justify-content-center text-primary"
        [pTooltip]="
          value.length === 1
            ? ('1 attached present' | translate)
            : value.length + (' attachments present' | translate)
        "
        showDelay="500"
        tooltipPosition="bottom"
        container="body"
        placement="left"
      ></i>
    </ng-template>

    <ng-template #availableQty let-value="value" let-item="item">
      <ng-container *ngVar="isProductQuantifiable(item) as isQuantifiable">
        <op-button-ng
          *ngIf="isQuantifiable"
          [ghost]="true"
          (buttonClick)="
            productShowQties.emit({ product: item, qtyType: 'availableQty' })
          "
          [tooltipLabel]="
            ('View orders linked to product' | translate) + ' ' + item.SKU
          "
          [color]="
            getScopeQtyNumber(item, 'availableQty') <= 0 ? 'danger' : undefined
          "
          [label]="getScopeQtyString(item, 'availableQty')"
        />
        <span *ngIf="!isQuantifiable"> - </span>
      </ng-container>
    </ng-template>

    <ng-template #barcodes let-value="value">
      <span
        [pTooltip]="parseBarcodes(value, true)"
        tooltipPosition="bottom"
        [fitContent]="false"
      >
        {{ parseBarcodes(value) }}
      </span>
    </ng-template>

    <ng-template #bulkUpdateError let-value="value">
      {{ value }}
      <div *ngIf="value?.error">
        <strong> {{ value.error?.code | translate }} </strong><br />
        {{ value.error?.message | translate }}
      </div>
    </ng-template>

    <ng-template #bulkUpdateResults let-value="value">
      <span
        class="badge"
        [ngClass]="{
          'badge-success': value === 'SUCCESS',
          'badge-danger': value === 'FAILED',
        }"
      >
        {{ value | translate }}
      </span>
    </ng-template>

    <ng-template #carrier let-value="value">
      <ng-container
        *ngIf="
          dataLookups && dataLookups['carriers']
            | find: '_id' : value as carrier
        "
        class="d-flex justify-content-center"
      >
        <op-carrier-logo
          [carrier]="carrier"
          size="sm"
          [centered]="true"
          [pTooltip]="carrier.customName"
          tooltipPosition="bottom"
        />
      </ng-container>
    </ng-template>

    <ng-template #catalogLocales let-value="value">
      <ng-container *ngVar="parseCatalogLocale(value) as name">
        <div [pTooltip]="name" tooltipPosition="bottom">{{ name }}</div>
      </ng-container>
    </ng-template>

    <ng-template #clipboard let-value="value">
      <div *ngIf="value" [opClipboard]="value">
        {{ value }}
      </div>
    </ng-template>

    <ng-template #compartment let-item="item">
      {{
        item
          | locationKind
            : (item?.place?.[4]?.type === 'TICKET_PALLET'
                ? 'TICKET_PALLET'
                : item?.place?.[4]?.type === 'TOTE'
                  ? 'TOTE'
                  : 'COMPARTMENT')
      }}
    </ng-template>

    <ng-template #completion let-value="value" let-item="item">
      <div
        class="chart-cell"
        *ngIf="item.rows | sum: 'orderedQty' as qtyOrdered"
      >
        <ng-container *ngIf="item.rows | sum: 'receivedQty' as qtyReceived">
          <p-progressBar
            [value]="(qtyReceived * 100) / qtyOrdered"
            [style]="{ height: '1.5rem' }"
            [showValue]="false"
            [pTooltip]="
              qtyReceived +
              ' / ' +
              qtyOrdered +
              ' (' +
              ((qtyReceived * 100) / qtyOrdered | number: '1.0-0') +
              '%)'
            "
            tooltipPosition="bottom"
            [ngStyle]="{
              '--progress-bar-bg':
                qtyOrdered > qtyReceived ? '#ffc107' : '#28a745',
            }"
          />
        </ng-container>
      </div>
    </ng-template>

    <ng-template #coverageOrder let-value="value">
      <ng-container *ngIf="dataLookups && dataLookups['coverage']">
        <span
          class="badge badge-status badge-{{ getOrderCoverage(value, true) }}"
        >
          {{ getOrderCoverage(value) }}
        </span>
      </ng-container>
    </ng-template>

    <ng-template #coverageOrderPicking let-value="value">
      <ng-container *ngIf="dataLookups && dataLookups['coverage']">
        <span
          class="badge badge-status badge-{{
            getPickingCoverage(value, true, true)
          }}"
        >
          {{ getPickingCoverage(value) }}
        </span>
      </ng-container>
    </ng-template>

    <ng-template #currency let-value="value">
      <ng-container *ngIf="dataLookups && dataLookups['currencies']">
        <div
          *ngIf="
            dataLookups['currencies'] | find: 'code' : value as currencyCode
          "
        >
          {{ parseCurrency(currencyCode) }}
        </div>
      </ng-container>
    </ng-template>

    <ng-template #familyCompleteness let-value="value">
      <ng-container *ngIf="{ p: familyCompletenessPercent(value) } as vm">
        <div class="chart-cell" *ngIf="vm.p !== null">
          <p-progressBar
            [value]="vm.p!"
            [style]="{ height: '1.5rem' }"
            [showValue]="false"
            [pTooltip]="(vm.p | number: '1.0-0') + '%'"
            tooltipPosition="bottom"
            [ngStyle]="{
              '--progress-bar-bg': vm.p === 100 ? '#28a745' : '#ffc107',
            }"
          />
        </div>
      </ng-container>
    </ng-template>

    <ng-template #goodsReceive let-value="value">
      <ng-container *ngFor="let reception of value">
        <div
          *ngIf="
            dataLookups && dataLookups['goodsReceives']
              | find: '_id' : reception._id as goodsReceive
          "
        >
          <a
            href="/goods-receive#goods-receive/{{ reception._id }}"
            class="badge badge-status mr-1 badge-{{
              goodsReceive.status | lowercase
            }}"
            target="_blank"
            [title]="
              dataLookups && dataLookups['goodsReceiveStatus']
                | find: 'code' : goodsReceive.status : 'name'
            "
          >
            {{ goodsReceive.header.orderNumber }}
          </a>
        </div>
      </ng-container>
    </ng-template>

    <ng-template #goodsReceiveByOrdNum let-value="value">
      <ng-container *ngVar="parseGoodsReceiveByNum(value) as goodsReceive">
        <div *ngIf="goodsReceive" [opClipboard]="goodsReceive">
          {{ goodsReceive }}
        </div>
      </ng-container>
    </ng-template>

    <ng-template #defaultValue let-value="value">
      <ng-container *ngIf="value && value.default">
        {{ value.default }}
      </ng-container>
    </ng-template>

    <ng-template #eecCountry let-value="value">
      <op-button-ng
        *ngIf="ueCountries.includes(value)"
        icon="fad fa-euro-sign icon-action"
        [tooltipLabel]="'EEC destination' | translate"
        tooltipPosition="bottom"
        [ghost]="true"
        color="secondary"
        class="d-flex justify-content-center"
      />
    </ng-template>

    <ng-template #executionStatus let-item="item">
      <ng-container *ngVar="getExecutionStatus(item) as status">
        <ng-container *ngIf="status">
          <span class="badge badge-status badge-{{ status.code | lowercase }}">
            {{ status.name | translate }}
          </span>
        </ng-container>
      </ng-container>
    </ng-template>

    <ng-template #goodsReceiveStatus let-value="value">
      <div
        *ngIf="
          dataLookups && dataLookups['goodsReceives']
            | find: '_id' : value as goodsReceive
        "
      >
        <span
          *ngIf="dataLookups && dataLookups['goodsReceiveStatus']"
          class="badge badge-status badge-{{ goodsReceive.status | lowercase }}"
        >
          {{
            dataLookups['goodsReceiveStatus']
              | find: 'code' : goodsReceive.status : 'name'
          }}
        </span>
      </div>
    </ng-template>

    <ng-template #image let-value="value" let-item="item">
      <ng-container *ngIf="item.file; else noImage">
        <div
          class="wrapper wrapper-image border rounded"
          [style.background-color]="item.color"
        >
          <img
            class="image"
            [attr.src]="imageUrl(item.file.path, item.tenantId)"
          />
        </div>
      </ng-container>

      <ng-template #noImage>
        <div
          [style.background-color]="item.color"
          class="wrapper wrapper-icon border rounded"
        >
          <i
            [style.color]="getFGColor(item.color)"
            class="icon fad fa-sign"
          ></i>
        </div>
      </ng-template>
    </ng-template>

    <ng-template #importantNote let-value="value" let-item="item">
      <op-button-ng
        *ngIf="item.header.importantNote === true"
        class="d-flex justify-content-center"
        icon="fad fa-exclamation-triangle"
        [ghost]="true"
        color="danger"
        [tooltipLabel]="item.header.notes"
        tooltipPosition="bottom"
      />
    </ng-template>

    <ng-template #incomingQty let-value="value" let-item="item">
      <ng-container *ngVar="isProductQuantifiable(item) as isQuantifiable">
        <op-button-ng
          *ngIf="isQuantifiable"
          [ghost]="true"
          (buttonClick)="
            productShowQties.emit({ product: item, qtyType: 'orderedQty' })
          "
          [tooltipLabel]="
            ('View ordered qties for product' | translate) + ' ' + item.SKU
          "
          [label]="getScopeQtyString(item, 'incomingQty')"
        />
        <span *ngIf="!isQuantifiable"> - </span>
      </ng-container>
    </ng-template>

    <ng-template #manufacturerName let-value="value">
      <div
        *ngVar="parseManufacturer(value) as name"
        [pTooltip]="name"
        tooltipPositionP="bottom"
      >
        {{ name }}
      </div>
    </ng-template>

    <ng-template #movedQty let-item="item">
      <ng-container *ngIf="item.sign !== undefined && item.qty !== undefined">
        <div
          class="font-weight-bold"
          [style.color]="item.sign === '+' ? '#28a745' : '#dc3545'"
        >
          {{ item.sign === '+' ? '+' : '' }}{{ item.qty }}
        </div>
      </ng-container>
    </ng-template>

    <ng-template #pickingListRef let-value="value" let-item="item">
      <op-button-ng
        *ngIf="
          dataLookups && dataLookups['pickingLists']
            | find: '_id' : value as pickingList
        "
        class="d-flex justify-content-center"
        icon="fad fa-box-full icon-action"
        [ghost]="true"
        color="secondary"
        [tooltipLabel]="pickingList.name"
        tooltipPosition="bottom"
      />
    </ng-template>

    <ng-template #pickProduct let-value="value" let-item="item">
      <div
        *ngIf="
          value.productType === 'SIMPLE' || value.productType === 'PACKAGE'
        "
        class="d-flex align-items-center flex-nowrap gap-1"
        [style.max-width.px]="180"
      >
        <op-input-number-ng
          key="pick-qty"
          [(value)]="item.shippedQty"
          [disabled]="true"
          [min]="0"
        />
        <op-button-ng
          *ngIf="item.orderedQty as orderedQty"
          class="ml-1"
          [color]="item.shippedQty !== orderedQty ? 'warning' : 'success'"
          [disabled]="item.shippedQty === orderedQty"
          (buttonClick)="
            pickProductQty
              ? pickProductQty(item)
              : pickSimpleProductQty
                ? pickSimpleProductQty(item)
                : undefined
          "
          [label]="'Pick' | translate"
        />
      </div>

      <div *ngIf="value.productType === 'KIT'">{{ '-' }}</div>
    </ng-template>

    <ng-template #position let-item="item">
      {{ item | locationKind: 'POSITION' }}
    </ng-template>

    <ng-template #notes let-value="value">
      <div
        class="d-flex justify-content-center align-items-center text-primary"
      >
        <i
          *ngIf="value?.length"
          class="fas fa-sticky-note icon-action"
          [pTooltip]="value"
          tooltipPosition="bottom"
        ></i>
      </div>
    </ng-template>

    <ng-template #onHandQty let-value="value" let-item="item">
      <ng-container *ngVar="isProductQuantifiable(item) as isQuantifiable">
        <op-button-ng
          *ngIf="isQuantifiable"
          [ghost]="true"
          (buttonClick)="
            productShowQties.emit({ product: item, qtyType: 'onHandQty' })
          "
          [tooltipLabel]="
            ('View stocks for product' | translate) + ' ' + item.SKU
          "
          [label]="getScopeQtyString(item, 'onHandQty')"
        />
        <span *ngIf="!isQuantifiable"> - </span>
      </ng-container>
    </ng-template>

    <ng-template #orderPackages let-value="value">
      <ng-container
        *ngVar="
          dataLookups && dataLookups['orders'] && dataLookups['orders']
            | find: 'header.rifOrder' : value as order
        "
      >
        {{ order?.packages?.length }}
      </ng-container>
    </ng-template>

    <ng-template #packedOrders let-value="value">
      {{
        (value ?? [] | filter: 'INCLUDES' : 'status' : ['PACKED', 'PRINTED'])
          .length
      }}
    </ng-template>

    <ng-template #packagesVolume let-value="value" let-item="item">
      {{ item.packages | sum: 'volume' }}
    </ng-template>

    <ng-template #packagesWeight let-value="value" let-item="item">
      {{ item.packages | sum: 'weight' }}
    </ng-template>

    <ng-template #packingActions let-value="value">
      {{ value | sum: 'qtyPacked' }}
    </ng-template>

    <ng-template #path let-value="value">
      {{ parseLocation(value) }}
    </ng-template>

    <ng-template #price let-value="value">
      {{
        value
          | currency
            : value?.header?.currency || currencyCode
            : 'symbol-narrow'
            : '1.2'
      }}
    </ng-template>

    <ng-template #priceWithCatalog let-value="value" let-item="item">
      {{ catalogLocale(value) }}
    </ng-template>

    <ng-template #priceWithFour let-value="value">
      {{
        value
          | currency
            : value?.header?.currency || currencyCode
            : 'symbol-narrow'
            : '1.4'
      }}
    </ng-template>

    <ng-template #priority let-value="value">
      {{ parsePriority(value) }}
    </ng-template>

    <ng-template #productCategories let-value="value">
      <div
        *ngVar="parseProductCategories(value) as categories"
        [pTooltip]="categories"
        tooltipPosition="bottom"
      >
        {{ categories }}
      </div>
    </ng-template>

    <ng-template #productLink let-value="value" let-item="item">
      <div [opClipboard]="value">
        <op-button-ng
          [ghost]="true"
          routerLink="/products"
          [fragment]="'product/' + item.product._id"
          target="_blank"
          [label]="value"
        />
      </div>
    </ng-template>

    <ng-template #productName let-value="value">
      {{ parseProductName(value) }}
    </ng-template>

    <ng-template #productParent let-value="value">
      <div
        *ngVar="parseProductParent(value) as parent"
        [pTooltip]="parent"
        tooltipPosition="bottom"
      >
        {{ parent }}
      </div>
    </ng-template>

    <ng-template #productPhoto let-item="item">
      <ng-container *ngIf="item.files && item.files.length; else noImage">
        <div class="wrapper wrapper-image border rounded">
          <img
            class="image"
            [attr.src]="imageUrl(item.files[0].path, item.tenantId)"
          />
        </div>
      </ng-container>
      <ng-template #noImage>
        <div class="wrapper wrapper-icon border rounded">
          <i class="icon fad fa-box-heart"></i>
        </div>
      </ng-template>
    </ng-template>

    <ng-template #productSites let-value="value">
      <span
        *ngIf="parseSites(value) as prodSites"
        [pTooltip]="prodSites"
        tooltipPosition="bottom"
      >
        {{ prodSites }}
      </span>
    </ng-template>

    <ng-template #productQties let-value="value" let-item="item">
      <div
        *ngIf="
          dataLookups && dataLookups['products'] && dataLookups['products']
            | find: '_id' : value as product
        "
      >
        <strong
          *ngIf="product.productType === 'SIMPLE'; else kitQty"
          [ngClass]="{
            'text-danger':
              (item.orderedQty && product.onHandQty < item.orderedQty) ||
              product.availableQty < 0,
          }"
        >
          {{ product.onHandQty }} ({{ product.availableQty }}) [{{
            product.incomingQty
          }}]
          {{ '{' + (product?.receivingQty || 0) + '}' }}
          <i
            *ngIf="
              (item.orderedQty && product.onHandQty < item.orderedQty) ||
              product.availableQty < 0
            "
            class="fas fa-exclamation-triangle"
          ></i>
        </strong>
        <ng-template #kitQty>-</ng-template>
      </div>
    </ng-template>

    <ng-template #qty let-value="value" let-item="item" let-column="column">
      <op-input-number-ng
        key="input-numer"
        [(value)]="item.qty"
        (valueChange)="updateQty(item, { _id: item._id, qty: $event })"
        [placeholder]="'Qty' | translate"
        [group]="true"
        [min]="0"
        [disableDecimals]="column.disableDecimals"
      />
    </ng-template>

    <ng-template #receivingQty let-value="value" let-item="item">
      <ng-container *ngVar="isProductQuantifiable(item) as isQuantifiable">
        <op-button-ng
          *ngIf="isQuantifiable"
          [ghost]="true"
          (buttonClick)="
            productShowQties.emit({ product: item, qtyType: 'incomingQty' })
          "
          [tooltipLabel]="
            ('View incoming qties for product' | translate) + ' ' + item.SKU
          "
          [label]="getScopeQtyString(item, 'orderedQty')"
        />
        <span *ngIf="!isQuantifiable"> - </span>
      </ng-container>
    </ng-template>

    <ng-template #returnedRowsCount let-value="value">
      {{ value ? (value | sum: 'returnedQty') : 0 }}
    </ng-template>

    <ng-template #role let-value="value" let-item="item">
      <ng-container *ngIf="dataLookups && dataLookups['roles']">
        {{ parseRoles(item.roles) }}
      </ng-container>
    </ng-template>

    <ng-template #rowsCount let-value="value">
      {{ value?.length }}
    </ng-template>

    <ng-template #rowsOrderedCount let-value="value">
      {{ value ? (value | sum: 'orderedQty') : 0 }}
    </ng-template>

    <ng-template #rowsReturnedCount let-value="value">
      {{ value ? (value | sum: 'returnedQty') : 0 }}
    </ng-template>

    <ng-template #rowsScrappedCount let-value="value">
      {{ value ? (value | sum: 'scrappedQty') : 0 }}
    </ng-template>

    <ng-template #sku let-value="value" let-item="item">
      <div class="d-flex align-items-center">
        <a
          *ngIf="item?.product"
          [opClipboard]="item.product?.SKU"
          (mousedown)="
            openProductInNewTab($event, item.product._id, item.product?.SKU)
          "
          style="
            cursor: pointer;
            text-decoration: underline;
            position: relative;
          "
        >
          {{ item.product.SKU }}
        </a>
        <i
          *ngIf="
            item.meta?.REGALO?.toUpperCase() === 'SI' ||
            item.meta?.REGALO?.toUpperCase() === 'SÌ'
          "
          class="fad fa-gift text-info ml-2"
          [pTooltip]="'Gift product' | translate"
          tooltipPosition="bottom"
          container="body"
          placement="right"
        ></i>
      </div>
    </ng-template>

    <ng-template #stHiPackedBy let-value="value">
      {{ parseStatusHistoryUser(value, 'PACKED') }}
    </ng-template>

    <ng-template #stHiProcessedBy let-value="value">
      {{ parseStatusHistoryUser(value, 'PROCESSED') }}
    </ng-template>

    <ng-template #suppliers let-value="value">
      <div
        *ngVar="parseSuppliersList(value) as suppliers"
        [pTooltip]="suppliers"
        tooltipPosition="bottom"
      >
        {{ suppliers }}
      </div>
    </ng-template>

    <ng-template #suppliersSku let-item="item">
      <ng-container *ngIf="item && item.suppliers">
        <div
          *ngIf="item.suppliers | join: 'SKU' : ', ' as supSkus"
          [pTooltip]="supSkus"
          tooltipPosition="bottom"
        >
          {{ supSkus }}
        </div>
      </ng-container>
    </ng-template>

    <ng-template #tagModel let-value="value" let-item="item">
      <ng-container *ngIf="item.value; else systemTag">
        <span
          class="badge badge-tag mr-1"
          [style.background-color]="item.color"
          [style.color]="item.color | colorNegative"
        >
          {{ value }}
        </span>
      </ng-container>
      <ng-template #systemTag>
        <span *ngIf="value?.length" class="badge badge-tag badge-dark mr-1">
          {{ value }}
        </span>
      </ng-template>
    </ng-template>

    <ng-template #tags let-value="value">
      <div class="d-flex flex-column align-items-start">
        <ng-container *ngFor="let tag of value; trackBy: trackByTag">
          <ng-container
            *ngIf="
              dataLookups && dataLookups['tags']
                | find: 'value' : tag as customTag;
              else sysTag
            "
          >
            <span
              class="badge badge-tag badge-secondary mb-1"
              [style.background-color]="customTag.color"
              [style.color]="
                !customTag.color ? '#fff' : (customTag.color | colorNegative)
              "
            >
              {{ tag }}
            </span>
          </ng-container>
          <ng-template #sysTag>
            <span *ngIf="tag?.length" class="badge badge-tag badge-dark mr-1">
              {{ tag }}
            </span>
          </ng-template>
        </ng-container>
      </div>
    </ng-template>

    <ng-template #sales let-value="value" let-item="item">
      <div *ngVar="item.sales ?? [] as sales" [opClipboard]="value">
        <op-button-ng
          [ghost]="true"
          [color]="!sales.length ? 'secondary' : undefined"
          (buttonClick)="
            !sales.length
              ? undefined
              : productShowQties.emit({ product: item, qtyType: 'sales' })
          "
          [tooltipLabel]="
            sales.length
              ? ('View sales for product' | translate) + ' ' + item.SKU
              : undefined
          "
          [label]="value"
        />
      </div>
    </ng-template>

    <ng-template #salesChannel let-item="item">
      {{ parseSalesChannel(item) }}
    </ng-template>

    <ng-template
      #selectRow
      let-value="value"
      let-column="column"
      let-rowIndex="rowIndex"
    >
      <div (click)="selectRowByIndex ? selectRowByIndex(rowIndex) : undefined">
        {{
          column.type === 'date'
            ? (value | date: column.dateFormat || 'dd/MM/yyyy HH:mm')
            : value
        }}
      </div>
    </ng-template>

    <ng-template #scrappedQty let-value="value" let-item="item">
      <ng-container *ngVar="isProductQuantifiable(item) as isQuantifiable">
        <op-button-ng
          *ngIf="isQuantifiable"
          [ghost]="true"
          (buttonClick)="
            productShowQties.emit({ product: item, qtyType: 'scrappedQty' })
          "
          [tooltipLabel]="
            ('View incoming qties for product' | translate) + ' ' + item.SKU
          "
          [label]="getScopeQtyString(item, 'scrappedQty')"
        />
        <span *ngIf="!isQuantifiable"> - </span>
      </ng-container>
    </ng-template>

    <ng-template #shelf let-item="item">
      {{ item | locationKind: 'SHELF' }}
    </ng-template>

    <ng-template #status let-value="value" let-item="item">
      <span class="badge badge-status badge-{{ value || '' | lowercase }}">
        {{
          (value === 'PACKED' && item.header?.orderNumber !== undefined
            ? 'Order Packed'
            : value
          ) | translate
        }}
      </span>
    </ng-template>

    <ng-template #statusHistoryTime let-value="value">
      <div *ngIf="value">{{ parseStatusHistoryTime(value) }}</div>
    </ng-template>

    <ng-template #tooltip let-value="value">
      <div [pTooltip]="value">
        {{ value }}
      </div>
    </ng-template>

    <ng-template #trackingNumber let-value="value">
      {{ parseTrackingNumber(value) }}
    </ng-template>

    <ng-template #unusableOrder let-value="value">
      <op-button-ng
        *ngIf="getUnusable(value)"
        color="danger"
        [ghost]="true"
        [tooltipLabel]="'Unusable order description' | translate"
        icon="fas fa-exclamation-triangle"
      />
    </ng-template>

    <ng-template #user let-value="value">
      {{ parseUserId(value) ?? value }}
    </ng-template>
  </p-table>
</div>

<!-- Custom External Paginator -->
<div
  *ngIf="!isError && data.length > 0 && !hidePaginator"
  class="custom-paginator-container"
  [class.paginator-sticky]="stickyPaginator"
>
  <div class="custom-paginator-content">
    <!-- Current page report -->
    <span class="paginator-text">
      {{ 'Page' | translate }} {{ ' ' }} {{ currentPage }} {{ ' '
      }}{{ 'of' | translate }} {{ ' ' }} {{ totalPages }} ({{
        totalCount || 0
      }}
      {{ ' ' }} {{ 'elements' | translate }})
    </span>

    <!-- Rows per page dropdown -->
    <div class="paginator-rows-selector">
      <p-dropdown
        [options]="rowsPerPageOptionsArray"
        [ngModel]="rows"
        (ngModelChange)="onRowsChange($event)"
        [style]="{ width: '80px' }"
        appendTo="body"
        [overlayOptions]="{ mode: 'overlay' }"
        [disabled]="isLoading"
      />
    </div>

    <!-- Navigation buttons -->
    <div class="paginator-navigation">
      <op-button-ng
        [outlined]="true"
        color="secondary"
        [ghost]="true"
        [square]="true"
        (buttonClick)="onPageChange(1)"
        icon="fas fa-angle-double-left"
        [disabled]="currentPage === 1 || isLoading"
      />
      <op-button-ng
        class="mr-2"
        (buttonClick)="onPageChange(currentPage - 1)"
        [outlined]="true"
        [ghost]="true"
        [square]="true"
        icon="fas fa-angle-left"
        [disabled]="currentPage === 1 || isLoading"
      />

      <!-- Page numbers -->
      <ng-container *ngFor="let page of visiblePages; trackBy: trackByPage">
        <!-- Ellipsis (non-clickable) -->
        <op-button-ng
          *ngIf="page === -1"
          [outlined]="true"
          color="secondary"
          [ghost]="true"
          label="..."
          [disabled]="isLoading"
        />

        <!-- Regular page button -->
        <op-button-ng
          *ngIf="page !== -1"
          [color]="page === currentPage ? 'primary' : 'secondary'"
          [outlined]="page !== currentPage"
          [class.active]="page === currentPage"
          (buttonClick)="onPageChange(page)"
          [square]="page.toString().length < 4"
          [label]="page.toString()"
          [disabled]="page === currentPage || isLoading"
        />
      </ng-container>

      <op-button-ng
        class="ml-2"
        (buttonClick)="onPageChange(currentPage + 1)"
        [outlined]="true"
        [ghost]="true"
        [square]="currentPage.toString().length < 4"
        icon="fas fa-angle-right"
        [disabled]="currentPage + 1 > totalPages || isLoading"
      />

      <op-button-ng
        [outlined]="true"
        color="secondary"
        [ghost]="true"
        [square]="currentPage.toString().length < 4"
        (buttonClick)="onPageChange(totalPages)"
        icon="fas fa-angle-double-right"
        [disabled]="currentPage + 1 > totalPages || isLoading"
      />
    </div>
  </div>
</div>

<!-- Error State -->
<div *ngIf="isError && !isLoading" class="error-state-container">
  <div class="error-state-content">
    <i class="fas fa-exclamation-triangle"></i>
    <h4>
      {{ 'Oops, it seems there was an error loading the elements' | translate }}
    </h4>
    <p>
      {{
        'Check that your internet connection is working and try reloading the page'
          | translate
      }}
    </p>
    <op-button-ng
      (buttonClick)="resetGrid()"
      [label]="'Reload page' | translate"
    />
  </div>
</div>

<!-- Empty State (no data at all) -->
<div *ngIf="showEmptyState && !isFiltered" class="empty-state-container">
  <div class="empty-state-content">
    <i class="far fa-plus-circle"></i>
    <h4>
      {{ 'There are no' | translate }} {{ ' ' }}
      {{ fileName.replaceAll('_', ' ') }}
    </h4>
    <p *ngIf="showCreateButton">
      {{ 'Create one in order to be able to carry out operations' | translate }}
    </p>
    <op-button-ng
      *ngIf="showCreateButton"
      (buttonClick)="itemCreate.emit()"
      [label]="('Create new' | translate) + ' ' + parseEntityName()"
    >
    </op-button-ng>
  </div>
</div>

<!-- Wrongly filtered -->
<div *ngIf="showEmptyState && !isFiltered" class="empty-state-container">
  <div class="empty-state-content">
    <i class="far fa-plus-circle"></i>
    <h4>
      {{ '-No' | translate }}{{ ' ' }} {{ fileName.replaceAll('_', ' ') }}
      {{ ' ' }}
      {{ 'matching your search parameters were found' | translate }}
    </h4>
    <op-button-ng (buttonClick)="resetGrid()" [label]="'Reset' | translate" />
  </div>
</div>
